import axios, { AxiosResponse } from "axios";
import { TenantUsage } from "src/layouts/AdminPortal/UsageLimits/TenantUsageMonitor.types";
import { getApiBaseUrl } from "src/config/Endpoints";
import { buildAuthHeaders, getAuthToken } from "./HttpUtils";

const API_BASE_URL = getApiBaseUrl();

// ========== Interfaces ==========

interface LoginPayload {
  email: string;
  password: string;
}

interface UpdateAdminPayload {
  target_email: string;
  new_user_name: string;
  new_role_name: string;
}

export interface AddAdminPayload {
  user_name: string;
  email: string;
  role_name: "Super Admin" | "Billing";
  password: string;
  confirm_password: string;
}
export interface UpdateTenantUsagePayload {
  tenant_id: number;
  chatbot_query_limit?: number;
  chatbot_token_limit?: number;
  email_quota?: number;
  template_generation_limit?: number;
  template_token_limit?: number;
  social_listening_analysis_limit?: number;
  social_listening_token_limit?: number;
}
interface ActivityLogPayload {
  start_date: string;
  end_date: string;
}

interface DashboardMetricsPayload {
  start_date: string;
  end_date: string;
}

interface CreateInvoicePayload {
  user_email: string;
  billing_address: string;
  package_name: string;
  package_price: number;
  next_billing_date: string;
  status: string;
  extra_users: number;
  price_per_extra_user: number;
  notes: string;
}

interface PipelineHealthPayload {
  client_name: string;
  start_date: string;
  end_date: string;
}

export interface AdminUser {
  user_name: string;
  email: string;
  user_role: string;
  tenant_name: string;
  signup_date: string;
  plan?: string;
  status: string;
  last_login: string;
}

// ========== Settings API Calls ==========

// Login
export const adminLogin = (payload: LoginPayload) => {
  return axios.post(`${API_BASE_URL}/admins/login`, payload);
};

// Get list of admins
export const getAdminList = () => {
  return axios.post(`${API_BASE_URL}/admins/list`, {}, { headers: buildAuthHeaders("admin_token") });
};

export const deleteAdmin = (payload: { target_email: string }) => {
  const authHeaders = buildAuthHeaders("admin_token"); // ✅ extract just the headers object
  return axios.delete(`${API_BASE_URL}/admins/delete`, {
    data: payload,
    headers: {
      ...authHeaders,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

// Update admin (edit)
export const updateAdmin = (payload: UpdateAdminPayload) => {
  return axios.post(`${API_BASE_URL}/admins/update`, payload, { headers: buildAuthHeaders("admin_token") });
};

// Add New Admin
export const addAdmin = (payload: AddAdminPayload) => {
  return axios.post(`${API_BASE_URL}/admins/add`, payload, { headers: buildAuthHeaders("admin_token") });
};

// Get Audit Logs List
export const getAuditLogs = () => {
  const token = getAuthToken("admin_token");
  return axios.get(`${API_BASE_URL}/admins/logs`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// ========== Activity Logs API Calls ==========
export const getUserActivityLogs = (payload: ActivityLogPayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/user-activity-logs`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

// ========== Dashbaord Logs API Calls ==========
export const getDashboardMetrics = (payload: DashboardMetricsPayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/dashboard-metrics`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

// Get current API health status
export const getAPIHealthStatus = () => {
  return axios.get(`${API_BASE_URL}/health-status`, { headers: buildAuthHeaders("admin_token") });
};

// Trigger a health re-check
export const triggerAPIHealthCheck = () => {
  return axios.get(`${API_BASE_URL}/health-check`, { headers: buildAuthHeaders("admin_token") });
};

// ========== System Alerts API Call ==========
export const getSystemAlerts = () => {
  return axios.post(
    `${API_BASE_URL}/admins/system-alerts`,
    {},
    { headers: buildAuthHeaders("admin_token") },
  );
};

// ========== Subscriptions API Call ==========

//Get existing invoices lsit
export const getInvoices = () => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices/list`,
    {},
    { headers: buildAuthHeaders("admin_token") },
  );
};

// Get Subscription Overview cards
export const getSubscriptionOverview = () => {
  return axios.post(
    `${API_BASE_URL}/admins/subscriptions/overview`,
    {},
    { headers: buildAuthHeaders("admin_token") },
  );
};

//get tenant list for create invoice page dropdowns
export const getTenants = () => {
  return axios.post(`${API_BASE_URL}/admins/tenants`, {}, { headers: buildAuthHeaders("admin_token") });
};

//create invoice
export const createInvoice = (payload: CreateInvoicePayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices/create`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

//view invoice
export const getInvoiceDetails = async (invoice_id: string) => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices`,
    { invoice_id },
    { headers: buildAuthHeaders("admin_token") },
  );
};

export const editInvoice = (payload: {
  invoice_id: string;
  billing_address: string;
  package_name: string;
  package_price: number;
  next_billing_date: string;
  status: string;
  extra_users: number;
  price_per_extra_user: number;
  notes: string;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices/edit`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

// Delete Invoice API
export const deleteInvoice = (invoice_id: string) => {
  return axios.delete(`${API_BASE_URL}/admins/invoices/delete`, {
    ...{ headers: buildAuthHeaders("admin_token") },
    data: { invoice_id }, // ✅ Body goes inside `data`
  });
};

// ========== Data Sources API Call ==========

// Get Connected Sources
export const getConnectedSources = () => {
  return axios.get(
    `${API_BASE_URL}/admins/connected-sources`,
    { headers: buildAuthHeaders("admin_token") },
  );
};

//sync connection
export const syncNow = (payload: {
  user_email: string;
  source_name: string;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/sync-now`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

//view connection
export const viewConnection = (data: {
  user_email: string;
  source_name: string;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/view-connection`,
    data,
    { headers: buildAuthHeaders("admin_token") },
  );
};

export const deleteConnectedSource = (payload: {
  user_email: string;
  source_name: string;
}) => {
  return axios.delete(`${API_BASE_URL}/admins/connected-sources`, {
    ...{ headers: buildAuthHeaders("admin_token") },
    data: payload, // ✅ This is how you send a body with DELETE
  });
};

// ========== Pipeline Health API Call ==========
export const getPipelineHealthData = (payload: PipelineHealthPayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/pipelines-data`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

// ========== Announcements API Call ==========

// Add this at the bottom of your file or near related endpoints
export const getAnnouncements = () => {
  return axios.get(`${API_BASE_URL}/admins/announcements`, { headers: buildAuthHeaders("admin_token") });
};

export const createAnnouncement = (payload: {
  title: string;
  content?: string;
  target_audience?: string;
  scheduled_for?: string;
  status: "scheduled" | "published" | "draft";
  url?: string; // ✅ Optional field added
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/announcements/create`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

export const deleteAnnouncement = (payload: { announcement_id: number }) => {
  return axios.delete(`${API_BASE_URL}/admins/announcements/delete`, {
    ...{ headers: buildAuthHeaders("admin_token") },
    data: payload,
  });
};

export const updateAnnouncement = (payload: {
  announcement_id: number;
  title: string;
  content: string;
  target_audience: string;
  scheduled_for: string | null;
  status: string;
  url?: string; // ✅ Optional field added
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/announcements/update`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

// ========== Users API Call ==========
export const getAllUsers = (): Promise<AxiosResponse<AdminUser[]>> => {
  return axios.get(`${API_BASE_URL}/admins/get-all-users`, { headers: buildAuthHeaders("admin_token") });
};

export const addPlatformUser = (payload: {
  user_name: string;
  user_email: string;
  tenant_email: string;
  status: string;
  features?: string[];
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/add-user`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

export const performUserAction = (payload: {
  user_id: number;
  user_name?: string;
  status?: string;
  delete?: boolean;
  suspend?: boolean;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/perform-action-on-user`,
    payload,
    { headers: buildAuthHeaders("admin_token") },
  );
};

export const resetUserPassword = async (user_email: string) => {
  return axios.post(
    `${API_BASE_URL}/admins/reset-user-password`,
    { user_email },
    { headers: buildAuthHeaders("admin_token") },
  );
};

// ========== Usage & Limits API Call ==========

// Fetch daily tenant usage
export const getTenantsDailyUsage = async () => {
  const apiKey = getAuthToken("admin_token");

  const res = await axios.post(
    `${API_BASE_URL}/admins/get-tenants-daily-usage`,
    {},
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "x-api-key": apiKey,
      },
    },
  );

  return res.data;
};

// Update tenant usage limits
export const updateTenantUsageLimits = async (
  payload: UpdateTenantUsagePayload,
) => {
  const apiKey = getAuthToken("admin_token");
  const response = await axios.post(
    `${API_BASE_URL}/admin/update-tenant-daily-usage`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "x-api-key": apiKey,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );

  return response.data;
};



